import { EventSource } from "../event-source";
import { View, ViewConstructor } from "../view";
import Router from "../router/index";
import { LazyTemplate } from "../template";
import * as Dom from "../helpers/dom";
import ViewRendeder from "../view/renderer";
import ActionBar from "../action-bar/index";
import ModalManager from "../modal/manager";
import Api, { ApiCallResponse, OfflineError } from "./api";
import ToastManager from "../toast/index";
import ViewProcessorManager from "../view/processor";
import FormViewProcessor from "../view/processor/form";
import FlipcardViewProcessor from "../view/processor/flipcard";
import AppHeader from "./header";
import DataBindViewProcessor from "../view/processor/data-bind";
import { AppSettings } from '../config';
import { BaseDatabase } from "../db/index";
import Synchronizer from "../synchronizer/index";
import { User } from "../db/models";
import { CurrentUser, AttributeKey } from "../db/models/attributes";
import ViewActions from "../view/actions";
import ErrorView from "../views/error/index";
import ErrorModal from "../modals/error/index";
import AuthenticationModal from "../modals/authentication/index";
import { InfoButton } from "../action-bar/items/index";

export abstract class BaseApp extends EventSource
{
    public currentViewType: ViewConstructor;
    public currentView: View;
    public settings: AppSettings;
    public constance: Constance;
    public defaultRoute = '/';
    public router: Router;
    public actionBar: ActionBar;
    public modals: ModalManager;
    public toasts: ToastManager;
    public viewProcessors: ViewProcessorManager;
    public api: Api;
    public actions: ViewActions;

    protected mainElement: HTMLElement;
    protected loadingElement: HTMLElement;

    public isAuthenticated: boolean;
    public viewContainer: HTMLElement;

    protected i18n: ClientSynci18n;

    protected errorView = ErrorView;
    public header: AppHeader;
    public db: BaseDatabase;
    public synchronizer: Synchronizer;
    public user: User;
    public userSettings: CurrentUser;

    public get apiToken(): string { return this.userSettings.token; }

    public configure(): PromiseLike<void>
    {
        doT.templateSettings = {
            evaluate:    /\{\{([\s\S]+?)\}\}/g,
            // encode and interpolate have been swapped, so that {{= }} defaults to encoded output.
            // use {{! }} for non-encoded output.
            encode:      /\{\{=([\s\S]+?)\}\}/g, 
            interpolate: /\{\{!([\s\S]+?)\}\}/g,
            use:         /\{\{#([\s\S]+?)\}\}/g,
            define:      /\{\{##\s*([\w\.$]+)\s*(\:|=)([\s\S]+?)#\}\}/g,
            conditional: /\{\{\?(\?)?\s*([\s\S]*?)\s*\}\}/g,
            iterate:     /\{\{~\s*(?:\}\}|([\s\S]+?)\s*\:\s*([\w$]+)\s*(?:\:\s*([\w$]+))?\s*\}\})/g,
            varname: 'it',
            strip: true,
            append: true,
            selfcontained: false
          };
        
        this.loadingElement = document.querySelector('aside.app-loading') as HTMLElement;
        this.mainElement = document.querySelector('main.app') as HTMLElement;

        let metaTag = document.querySelector('meta[name="application-settings"]');
        let data = metaTag.getAttribute('content');
        let decoded = atob(data);
        
        this.settings = JSON.parse(decoded);
        this.api = new Api(this, this.settings.trackingcloudUrl);
        this.actionBar = new ActionBar(this);
        this.modals = new ModalManager(this);
        this.toasts = new ToastManager(this);
        this.router = new Router(this);
        this.viewProcessors = new ViewProcessorManager(this);
        this.header = new AppHeader(this);
        this.actions = new ViewActions(this);
        
        this.configureRouter(this.router);
        this.configureViewProcessors(this.viewProcessors);

        this.viewContainer = <HTMLElement>document.querySelector('[data-view-container]');
        
        return this.initDatabase().then(() =>
        {
            return this.authenticate();
        }).then((authenticated) => 
        {
            this.configureActionbar(this.actionBar);
            this.isAuthenticated = authenticated;
            return this.db.Attribute.get(AttributeKey.CONSTANCE);
        }).then((constance) =>
        {
            this.constance = constance != null ? (constance as any).value : {};
        });
    }

    protected initDatabase(): PromiseLike<void>
    {
        return new Promise((resolve, reject) =>
        {
            if (this.db !== undefined)
            {
                this.db.open().then(() => resolve());
            }
            else
            {
                resolve();
            }
        });
    }

    public init(): PromiseLike<void>
    {
        return Promise.resolve();
    }

    constructor()
    {
        super();

        this.configure().then(() =>
        {
            return this.init();
        }).then(() =>
        {
            
            if (this.isAuthenticated)
            {
                this.synchronizer = new Synchronizer(this);
                this.synchronizer.update(true).then(() => 
                {
                    this.db.User.get(this.userSettings.id).then((user) =>
                    {
                        this.user = user;
                        this.router.run().then(() =>
                        {
                            this.isLoading = false;
                        });
                    });
                }, (error) =>
                {
                    this.error(error, new Error('Client synchronization failed.'));
                });
            }
        });
    }

    private unloadCurrentView()
    {
        let unloadPromise: PromiseLike<void>;
        
        if (this.currentView !== undefined && !this.currentView.isUnloaded)
        {
            unloadPromise = this.currentView.unload();
        }
        else
        {
            unloadPromise = Promise.resolve();
        }

        return unloadPromise;
    }
    
    public view(viewType: ViewConstructor, data?: any): PromiseLike<View>
    {
        return new Promise((resolve, reject) =>
        {
            this.unloadCurrentView().then(() =>
            {
                this.currentViewType = viewType;
                this.currentView = new viewType(data);
                this.renderView(this.currentView, this.viewContainer).then(() =>
                {
                    resolve(this.currentView);
                });
            });
        });
    }

    public renderView(view: View, containerElement?: HTMLElement, clearContainer = true): PromiseLike<void>
    {
        view.app = this;

        return this.viewProcessors.beforeRender(view).then(() =>
        {
            return view.beforeRender();
        }).then(() =>
        {
            return ViewRendeder.renderToElement(view);
        }).then(() =>
        {
            if (containerElement !== undefined)
            {
                if (clearContainer)
                {
                    Dom.emptyElement(containerElement);
                }

                containerElement.appendChild(view.element);
            }

            return this.viewProcessors.afterRender(view);
        }).then(() =>
        {
            return view.afterRender();
        });
    }

    public refreshCurrentView(data?: any): PromiseLike<void>
    {
        data = data !== undefined ? data: this.currentView.data;
        return this.view(this.currentViewType, data).then(() =>
        {

        });
    }

    public renderPartialView(view: View, containerElement?: HTMLElement): PromiseLike<void>
    {
        return this.renderView(view, containerElement, false);
    }

    public setFormEnabled(form: HTMLFormElement, enabled: boolean)
    {
        const formControls = form.querySelectorAll('input, textarea, button, select');
        
        for (let control of formControls)
        {
            if (!control.hasAttribute('data-no-auto-disable'))
            {
                if (enabled)
                {
                    control.removeAttribute('disabled');
                }
                else
                {
                    control.setAttribute('disabled', '');
                }
            }
        }

        const submitButtons =  form.querySelectorAll('[type="submit"]');

        for(let button of submitButtons)
        {
            if (enabled)
            {
                button.classList.remove('loading');
            }
            else
            {
                button.classList.add('loading');
            }
        }
    }

    public error(error: Error, displayError?: Error)
    {
        this.isLoading = false;
        console.error(error);        
        this.modals.show(new ErrorModal(displayError, error));
    }

    public warning(error: Error, actualError?: Error)
    {
        console.warn(actualError !== undefined ? actualError : error);
        this.view(this.errorView, error);
    }

    protected requesti18n(languageCode: string): PromiseLike<void>
    {
        return this.api.public(this.settings.clientSync.apiEndpoints.i18n, {language_code: languageCode}).then((i18n: ClientSynci18n) =>
        {
            this.i18n = i18n;
        }, (error) =>
        {
            if (error instanceof OfflineError)
            {
                return Promise.resolve();
            }
            else
            {
                console.error(error);
            }
        });
    }

    public authenticate(): PromiseLike<boolean>
    {
        return new Promise((resolve, reject) =>
        {
            if (this.settings.emulateAuthentication)
            {
                resolve(true);
            }
            else
            {
                return this.db.Attribute.get(AttributeKey.CURRENT_USER).then((user: any) =>
                {
                    if (user)
                    {
                        this.userSettings = user;
                        this.requesti18n(this.userSettings.language).then(() => {resolve(true)});
                    }
                    else
                    {
                        this.requesti18n(navigator.language).then(() =>
                        {
                            this.performAuthentication().then(() => {resolve(true)});
                        }); 
                    }
                });
            }
        });
    }

    protected performAuthentication(): PromiseLike<void>
    {
        this.isLoading = false;
        return this.modals.show(new AuthenticationModal()).then(() =>
        {
            return this.requesti18n(this.userSettings.language).then(() =>
            {
                this.toasts.show(this.pgettext('Message', 'Successfully signed in!'));
            });
        });
    }

    public configureRouter(router: Router)
    {

    }

    public configureActionbar(actionBar: ActionBar): void
    {
        actionBar.add(InfoButton);
    }

    public configureViewProcessors(processors: ViewProcessorManager)
    {
        processors.add(FormViewProcessor);
        processors.add(DataBindViewProcessor);
    }

    public logout(): void
    {
        this.db.Attribute.delete(AttributeKey.CURRENT_USER);
        this.refresh();
    }

    public refresh()
    {
        window.location.reload();
    }

    public formatString(str: string, args?: StringFormatArgs)
    {
        if (args != null)
        {
            return str.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key) => 
            {
                if (args.hasOwnProperty(key))
                {
                    return args[key].toString();
                } else 
                {
                    return match;
                }
            });
        }
        else
        {
            return str;
        }
    }

    public pgettext(context: string, message: string, args?: StringFormatArgs): string
    {
        let key = `${context}:${message}`;

        if (this.i18n != null && this.i18n.translations[key] !== undefined)
        {
            return this.formatString(this.i18n.translations[key], args);
        }
        else
        {
            console.warn(`Unknown translation: "${message}" (context: "${context}"), args: `, args);
            return this.formatString(message, args);
        }
    }

    public ugettext(message: string, args?: StringFormatArgs): string
    {
        return this.pgettext('', message, args);
    }

    public get isLoading(): boolean
    {
        return this.loadingElement.classList.contains('visible');
    }

    public set isLoading(value: boolean)
    {
        if (value)
        {
            document.body.classList.add('loading');
        }
        else
        {
            document.body.classList.remove('loading');
        }
    }

    protected onRefreshRequired = () =>
    {
        this.promptInstallUpdate();
    };

    public promptInstallUpdate()
    {
        let html = `
        ${this.pgettext('Message', 'Update ready to install')}<br />
        <small>${this.pgettext('Message', 'Click to begin')}</small>`;

        this.toasts.show(html, 0, 'im im-spinner8 spin', true, false).then(() =>
        {
            location.reload();
        });
    }    
    
    public promptDownloadUpdate()
    {
        let html = `
        ${this.pgettext('Message', 'Update available')}<br />
        <small>${this.pgettext('Message', 'Click to download')}</small>`;

        this.toasts.show(html, 0, 'im im-cloud-download', true, false).then(() =>
        {
            location.reload();
        });
    }
}

export interface ClientSynci18n extends ApiCallResponse
{
    language_code: string;
    translations: {[key: string]: string};
}

export interface StringFormatArgs
{
    [key: string]: number | string;
}

export interface Constance
{
    [key: string]: Object;
}